import { FC, useMemo, useState, useCallback, useEffect, useRef } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import {
  Box,
  Button,
  CircularProgress,
  SxProps,
  Typography,
  Theme,
  Menu,
  MenuItem,
} from "@mui/material";
import { makeSxStyles, ControlledSelect, ConfirmDialog } from "ui";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ManageAccountsOutlined,
  RefreshOutlined,
  RestoreOutlined,
  SendOutlined,
  TurnSharpRightOutlined,
} from "@mui/icons-material";
import { DateTime } from "luxon";
import { shallow } from "zustand/shallow";

import { toast } from "react-toastify";
import MapComponent from "../../../components/map/map";
import useRoutePlannerStore, {
  Modals,
} from "../../../state/route-planner-store";
import RoutePlannerTable from "./route-planner-table";
import { useAddressClickHandler } from "../../../shared/hooks/use-address-click-handler";
import { adminPatchRouteStartTimeReqDtoSchema, Nullable } from "service";
import { SortOptions } from "../components/generic-table/generic-table";
import { merge } from "lodash";
import { RoutePlannerMapFilters } from "./map-filters";
import {
  MapComponentProps,
  MapMarker,
  OrderStatuses,
} from "../../../components/map/map.types";
import SelectedAddresesSidebar from "../../../components/map/selected-addresses-sidebar";
import { RouteStatus } from "./helpers/route-status.enum"; // You'll need to create this enum
import { stopServiceMinutes } from "./helpers/stop-service-minutes";
import BulkEditStartTimeForm from "./planning/bulk-edit-start-time-form";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useFormik } from "formik";
import {
  AdminGetRoutesLocationCustomerOrderResDto,
  AdminGetRoutesLocationsResDto,
  AdminGetRoutesResDto,
  AdminPatchRouteStartTimeReqDto,
} from "service/api/estabrookSchemas";
import { BulkEditStartTimeDialog } from "./planning/bulk-edit-start-time";
import { MapActionTypeEnum } from "service/types/enum/route-planner-action-type-enum";
import { EditRouteDriverDialog } from "./planning/edit-route-driver";
import {
  getSelectionType,
  categorizeAddresses,
  isUnassignedAddress,
} from "../../../shared/utils/address-assignment";
import { RoutePlannerNotificationModals } from "../../../features/route-planning/components/route-planner-notification-modals";
import CreateVirtualRouteDialog from "./create-virtual-route-dialog";
import {
  getBulkActions,
  useFilteredRouteOptions,
} from "../../../utils/route-status-options";
import MapProvider from "../../../components/map/map-provider";
import { capitalizeFirstLetter } from "../../../utils/string-helpers";
import TransitionRoutesPhaseModal from "./transition-routes-modal";
import AddCustomerModal from "../../../components/add-customer-modal/add-customer-modal";
import { ReverseSequenceDialog } from "./build/reverse-sequence-dialog";
import { EndShiftDialog } from "./live/end-shift-dialog";
import MoveLocationsDialog from "./build/move-locations-dialog";
import LoadingModal from "./loading-modal";
import { hasInProgressLocations } from "../../../utils/route-planner";
import { ZERO_SNOW_AMOUNT } from "../../../utils/measurement";
import SocketContextProvider from "../../../web-sockets/web-socket-provider";
import useRoutePlannerApi, {
  AvailableStatuses,
} from "../../../hooks/use-route-planner-api";
import Navbar from "../../../components/navbar/navbar";
import RoutePlannerToolbarActions from "./route-planner-toolbar-actions";
import useMapResizing from "../../../shared/hooks/useMapResizing";
import { AdRoutes } from "../../../constants/ad-routes";
import { useRouteStartTimeValidation } from "../../../hooks/use-route-start-time-validation";
import ClickableDisabledButton from "../../../components/clickable-disabled-button";
import { useTimeSynchronization } from "../../../hooks/use-time-synchronization";
import { useScrollSelectedOrderIntoView } from "../../../hooks/use-scroll-selected-order-into-view";
import { useInitialRoutePlannerDate } from "../../../hooks/use-initial-route-planner-date";

const RoutePlannerPage: FC = () => {
  const styles = useStyles();

  const navigate = useNavigate();
  const { date } = useParams();
  const initialDate = date
    ? DateTime.fromFormat(date, "MM-dd-yyyy")
    : DateTime.now();
  const [selectedDate, setSelectedDate] = useState<DateTime>(initialDate);
  const [endShiftStatus, setEndShiftStatus] = useState<
    "loading" | "success" | undefined
  >(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);

  // Local state for context menu to avoid Zustand infinite rerenders
  const [contextMenuState, setContextMenuState] = useState<{
    open: boolean;
    position: { top: number; left: number } | null;
    mixedSelection: MapMarker[];
  }>({
    open: false,
    position: null,
    mixedSelection: [],
  });

  const [params] = useSearchParams();
  const {
    routeLocked,
    routes,
    sidebar,
    mapHeight,
    mapZoom,
    addresses,
    drawing,
    lassoMode,
    mapCentering,
    setMapCentering,
    query,
    // virtualRoute, // FIXME add this to route when BE is ready
    selectedAddresses,
    pendingDriverUpdate,
    pendingNotificationUpdate,
    pendingVirtualRouteDelete,
    pendingUnassignedMove,
    setPendingUnassignedMove,
    setQuery,
    setRouteLocked,
    setMapZoom,
    setDate,
    resetAllSelected,
    setDirectSelection,
    removeSelected,
    setSelectedAddress,
    setSidebar,
    setRoutes,
    toggleDrawing,
    setMapHeight,
    moveDialog,
    setMoveDialog,
    moveOrdersDialogOpen,
    moveOrders,
    setMoveOrdersDialogOpen,
    validSequence,
    sameRegionId,
    setPendingDriverUpdate,
    setPendingVirtualRouteDelete,
    setNewVirtualRoutePriority,
    isLoadingModalShown,
    setIsLoadingModalShown,
    shouldExcludeLocation,
    transitioningRoutesPhase,
    setTransitioningRoutesPhase,
    driverMarkers,
    activeModal,
    setActiveModal,
    hasLiveStateChanges,
    hasPlanningChanges,
    setHasPlanningChanges,
    searchedAddressForCustomer,
    setSearchedAddressForCustomer,
    isCalculatingEstTime,
    isCommittingLiveRoutes,
  } = useRoutePlannerStore(
    (state) => ({
      routeLocked: state.routeLocked,
      routes: state.routes,
      sidebar: state.sidebar,
      mapHeight: state.mapHeight,
      mapZoom: state.mapZoom,
      addresses: state.addresses,
      drawing: state.drawing,
      lassoMode: state.lassoMode,
      mapCentering: state.mapCentering,
      setMapCentering: state.setMapCentering,
      query: state.query,
      selectedAddresses: state.selectedAddresses,
      pendingDriverUpdate: state.pendingDriverUpdate,
      pendingNotificationUpdate: state.pendingNotificationUpdate,
      pendingVirtualRouteDelete: state.pendingVirtualRouteDelete,
      pendingUnassignedMove: state.pendingUnassignedMove,
      setPendingUnassignedMove: state.setPendingUnassignedMove,
      setQuery: state.setQuery,
      setRouteLocked: state.setRouteLocked,
      setMapZoom: state.setMapZoom,
      setDate: state.setDate,
      resetAllSelected: state.resetAllSelected,
      setDirectSelection: state.setDirectSelection,
      removeSelected: state.removeSelected,
      setSelectedAddress: state.setSelectedAddress,
      setSidebar: state.setSidebar,
      setRoutes: state.setRoutes,
      toggleDrawing: state.toggleDrawing,
      setMapHeight: state.setMapHeight,
      moveDialog: state.moveDialog,
      setMoveDialog: state.setMoveDialog,
      moveOrdersDialogOpen: state.moveOrdersDialogOpen,
      moveOrders: state.moveOrders,
      setMoveOrdersDialogOpen: state.setMoveOrdersDialogOpen,
      validSequence: state.validSequence,
      sameRegionId: state.sameRegionId,
      setPendingDriverUpdate: state.setPendingDriverUpdate,
      setPendingVirtualRouteDelete: state.setPendingVirtualRouteDelete,
      setNewVirtualRoutePriority: state.setNewVirtualRoutePriority,
      isLoadingModalShown: state.isLoadingModalShown,
      setIsLoadingModalShown: state.setIsLoadingModalShown,
      shouldExcludeLocation: state.shouldExcludeLocation,
      transitioningRoutesPhase: state.transitioningRoutesPhase,
      setTransitioningRoutesPhase: state.setTransitioningRoutesPhase,
      driverMarkers: state.driverMarkers,
      activeModal: state.activeModal,
      setActiveModal: state.setActiveModal,
      hasLiveStateChanges: state.hasLiveStateChanges,
      hasPlanningChanges: state.hasPlanningChanges,
      setHasPlanningChanges: state.setHasPlanningChanges,
      searchedAddressForCustomer: state.searchedAddressForCustomer,
      setSearchedAddressForCustomer: state.setSearchedAddressForCustomer,
      isCalculatingEstTime: state.isCalculatingEstTime,
      isCommittingLiveRoutes: state.isCommittingLiveRoutes,
    }),
    shallow,
  );

  // Initialize centralized time synchronization for the route planner
  // All components should read syncedCurrentTime from the store instead of using local state
  useTimeSynchronization();
  useScrollSelectedOrderIntoView();

  const addressesRef = useRef(addresses);
  const routesRef = useRef(routes);
  const selectedAddressesRef = useRef(selectedAddresses);

  useEffect(() => {
    addressesRef.current = addresses;
  }, [addresses]);

  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  useEffect(() => {
    selectedAddressesRef.current = selectedAddresses;
  }, [selectedAddresses]);

  // Handle initial navigation to the appropriate date (live > planned > today)
  const { isRedirecting } = useInitialRoutePlannerDate(date);

  // Sync selectedDate with URL param when it changes (e.g., after redirect)
  useEffect(() => {
    if (date) {
      const parsedDate = DateTime.fromFormat(date, "MM-dd-yyyy");
      if (parsedDate.isValid && !parsedDate.equals(selectedDate)) {
        setSelectedDate(parsedDate);
      }
    }
  }, [date]);

  // Keep the store's `date` in sync with the on-screen day (incl. silent
  // auto-navigations like End Shift), so flows like create-temp-route can
  // detect drift. Use the formatted string, not the DateTime object, since
  // DateTime instances aren't referentially stable across renders.
  const selectedDateKey = selectedDate.toFormat("yyyy-MM-dd");
  useEffect(() => {
    setDate(selectedDateKey);
  }, [selectedDateKey, setDate]);

  const {
    routesData: _routesData, // Used internally by hook to set filtered map markers (addresses)
    unfilteredRoutesData, // Unfiltered routes data (used for table and route status)
    isLoading,
    refetch,
    liveRouteData,
    plannedRouteData,
    refetchLiveRouteData,
    refetchPlannedRouteData,
    isLoadingSearchData,
    isFetchingSearchData,
    searchData,
    updateDriverStatuses,
    transferOrders,
    endShift,
    reverseSequence,
    updateRouteCustomerOrders,
    bulkEditStartTime,
    commit,
    reset,
    createRoutes,
    deleteVirtualRoute,
    dispatchRoutes,
    rollback,
    updateRoute,
    updateRouteTime,
    updateRouteLock,
    queryParams,
    setQueryParams,
    revertToPlanning,
    // Notification functions
    globalNotificationSettings,
    triggerEstCalculation,
  } = useRoutePlannerApi({
    date: date as string,
  });

  const onRefreshRoute = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const { onMouseDown, isDragging } = useMapResizing({
    minHeight: 70,
    maxHeight: window.innerHeight - 190, // Account for navbar (120px), drag handle (~30px), and minimum table space (~100px)
    setHeight: setMapHeight,
  });

  const openModal = useCallback(
    (modalName: Modals) => setActiveModal(modalName),
    [setActiveModal],
  );
  const closeModal = useCallback(() => {
    setActiveModal(null);
    // Reset end shift status to prevent stale state when reopening
    setEndShiftStatus(undefined);
  }, [setActiveModal]);

  const notCompletedOrders = (unfilteredRoutesData?.data || []).reduce(
    (total, route) =>
      total +
      (route.locations?.filter(
        (location) => location.driverStatus !== "completed",
      ).length || 0),
    0,
  );

  const showLoadingScreenAfterThreeSeconds = (): NodeJS.Timeout => {
    return setTimeout(() => {
      setIsLoadingModalShown(true);
    }, 3000);
  };

  const clearTimeoutAndHideModal = (timeout: NodeJS.Timeout) => {
    clearTimeout(timeout);
    setIsLoadingModalShown(false);
  };

  const { currentRouteStatus } = useFilteredRouteOptions(unfilteredRoutesData);
  const [adjustedServiceTimeMap, setAdjustedServiceTimeMap] = useState<
    Record<string, number>
  >({});
  const [adjustedEstimatedTimeMinutesMap, setAdjustedEstimatedTimeMinutesMap] =
    useState<Record<string, number>>({});

  // Validate route start times in real-time
  const { hasInvalidStartTime } = useRouteStartTimeValidation({
    routes: unfilteredRoutesData?.data || [],
    currentRouteStatus,
  });

  // Determine if any selected addresses are unassigned for validation purposes
  const { hasUnassigned } = categorizeAddresses(selectedAddresses);
  const hasAnyUnassignedAddresses = hasUnassigned;

  // Each stop's own service minutes from the cumulative serviceTimeMin, read
  // in the order the server lists a route's stops.
  const serviceTimeMap = useMemo(() => {
    const map: Record<string, number> = {};

    routes.forEach((route) => {
      if (route.id === "unassigned-route" || !route.locations) return;
      Object.assign(map, stopServiceMinutes(route.locations));
    });

    return map;
  }, [routes]);

  // Create enhanced routes data that includes unassigned route when needed
  const enhancedRoutesData = useMemo(() => {
    if (!routes || routes.length === 0) return routes;

    // Check if there are selected unassigned addresses (from user interaction, not filters)
    const selectedUnassignedAddresses =
      selectedAddresses.filter(isUnassignedAddress);

    // Only show unassigned route if there are actually selected unassigned addresses
    // This should only happen when user manually selects addresses (lasso, click, etc.)
    // AND the user is not just filtering (filters should only affect map, not table)
    if (selectedUnassignedAddresses.length === 0) {
      return routes;
    }

    // Keep all selected unassigned addresses in the unassigned route
    // The drag operation will handle moving them properly
    const remainingUnassignedAddresses = selectedUnassignedAddresses;

    // Only remove the unassigned route if there are no selected addresses at all
    if (remainingUnassignedAddresses.length === 0) {
      return routes.filter((route) => route.id !== "unassigned-route");
    }

    // Check if there's already an unassigned route in the routes
    const existingUnassignedRoute = routes.find(
      (route) => route.id === "unassigned-route",
    );

    // If there's already an unassigned route, update it instead of creating a new one
    if (existingUnassignedRoute) {
      const updatedUnassignedRoute = {
        ...existingUnassignedRoute,
        locations: remainingUnassignedAddresses.map((address) => ({
          locationId: address.id,
          latitude: address.latitude || 0,
          longitude: address.longitude || 0,
          address: address.address || "",
          locationSequence: -1, // Use -1 to indicate unassigned locations for icon rendering
          serviceTime:
            typeof address.duration === "number"
              ? address.duration
              : typeof address.duration === "string"
                ? parseInt(address.duration) || 15
                : 15,
          customer: address.customer || undefined,
          order: {
            status: address.order?.status ?? "regular",
            customerSkipOverridden: false,
          } as AdminGetRoutesLocationsResDto["order"],
          driverStatus: address.driverStatus || undefined,
          driverStatusLastUpdatedAt:
            address.driverStatusLastUpdatedAt || undefined,
          type: "onDemand" as const, // Required field with correct type
          snowAmount: ZERO_SNOW_AMOUNT,
        })),
      };

      // Replace the existing unassigned route with the updated one
      return routes.map((route) =>
        route.id === "unassigned-route" ? updatedUnassignedRoute : route,
      );
    }

    // Create fake unassigned route object for the table (only when addresses are selected)
    const unassignedRoute: AdminGetRoutesResDto = {
      id: "unassigned-route",
      routeName: "Unassigned",
      color: "stone",
      regionId: null,
      region: {
        id: "unassigned",
        name: "Unassigned",
        color: "stone", // Use stone color like in region management
      },
      driverId: null,
      driver: undefined,
      availableForAnyDriver: false,
      vehicleId: null,
      date: selectedDate.toISO() || "",
      routePriority: -1, // Put it first
      priorityByDriver: -1,
      state: "planning",
      startTime: "",
      isLocked: false,
      isVirtual: false,
      locations: remainingUnassignedAddresses.map((address) => ({
        locationId: address.id,
        latitude: address.latitude || 0,
        longitude: address.longitude || 0,
        address: address.address || "",
        locationSequence: -1, // Use -1 to indicate unassigned locations for icon rendering
        serviceTime:
          typeof address.duration === "number"
            ? address.duration
            : typeof address.duration === "string"
              ? parseInt(address.duration) || 15
              : 15,
        customer: address.customer || undefined,
        order: {
          status: address.order?.status ?? "regular",
          customerSkipOverridden: false,
        } as AdminGetRoutesLocationsResDto["order"],
        driverStatus: address.driverStatus || undefined,
        driverStatusLastUpdatedAt:
          address.driverStatusLastUpdatedAt || undefined,
        type: "onDemand" as const, // Required field with correct type
        snowAmount: ZERO_SNOW_AMOUNT,
      })),
      travelDetails: undefined,
      startingPoint: undefined,
      notificationSettings: undefined,
    };

    // Return routes with unassigned route as first item (original routes unchanged)
    return [unassignedRoute, ...routes];
  }, [routes, selectedAddresses, selectedDate]);

  // Reset planning changes and end shift status when date changes
  useEffect(() => {
    setHasPlanningChanges(false);
    setEndShiftStatus(undefined);
  }, [selectedDate.toFormat("yyyy-MM-dd")]);

  // Reset selection state when navigating away from route planner
  useEffect(() => {
    return () => {
      // Cleanup: reset selected addresses and sidebar when component unmounts
      resetAllSelected();
    };
  }, [resetAllSelected, date]);

  // Auto-focus on newly created customer when addresses update
  useEffect(() => {
    if (searchedAddressForCustomer && addresses.length > 0) {
      // Find the new address by the stored address string
      const newAddress = addresses.find(
        (addr) => addr.address === searchedAddressForCustomer,
      );

      if (newAddress) {
        // Focus the map on the new address and select it
        setMapCentering(undefined, [newAddress]);
        setSelectedAddress([newAddress], false);
        setSidebar(true);

        // Clear the stored address so we don't keep focusing on it
        setSearchedAddressForCustomer("");
      }
    }
  }, [
    addresses,
    searchedAddressForCustomer,
    setMapCentering,
    setSelectedAddress,
    setSidebar,
    setSearchedAddressForCustomer,
  ]);

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
  );

  const { setClickedAddress } = useAddressClickHandler();

  const handleDateChange = (date: DateTime | null) => {
    if (date && date.isValid) {
      const formattedDate = date.toFormat("yyyy-MM-dd");
      setSelectedDate(date);
      setQueryParams({
        ...queryParams,
        date: formattedDate,
      });
      navigate(`/route-planner/${date.toFormat("MM-dd-yyyy")}`, {
        state: { internalNavigation: true },
      });
    }
  };

  const handleStatusChange = useCallback(
    (status: AvailableStatuses, isChecked: boolean) => {
      setQueryParams((prevParams) => {
        const updatedStatuses = isChecked
          ? [...(prevParams.orderStatuses || []), status]
          : (prevParams.orderStatuses || []).filter((s) => s !== status);

        return {
          ...prevParams,
          orderStatuses: updatedStatuses,
        };
      });
    },
    [setQueryParams],
  );

  const handleLocationTypeChange = useCallback(
    (locationType: "onDemand" | "annual", isChecked: boolean) => {
      setQueryParams((prevParams) => {
        const updatedLocationTypes = isChecked
          ? [...(prevParams.locationTypes || []), locationType]
          : (prevParams.locationTypes || []).filter((t) => t !== locationType);

        return {
          ...prevParams,
          locationTypes: updatedLocationTypes,
        };
      });
    },
    [setQueryParams],
  );

  const handleUnassignedFilterChange = useCallback(
    (filterType: "active" | "inactive", isChecked: boolean) => {
      setQueryParams((prevParams) => {
        const currentUnassignedActive = prevParams.unassignedActive || false;
        const currentUnassignedInactive =
          prevParams.unassignedInactive || false;

        let newUnassignedActive = currentUnassignedActive;
        let newUnassignedInactive = currentUnassignedInactive;

        if (filterType === "active") {
          newUnassignedActive = isChecked;
        } else {
          newUnassignedInactive = isChecked;
        }

        return {
          ...prevParams,
          unassignedActive: newUnassignedActive,
          unassignedInactive: newUnassignedInactive,
        };
      });
    },
    [setQueryParams],
  );

  const lockRoute = async () => {
    const loadingTimeout = showLoadingScreenAfterThreeSeconds();
    try {
      await updateRouteLock({
        body: {
          isLocked: !routeLocked,
        },
        pathParams: {
          date: selectedDate.toFormat("yyyy-MM-dd"),
        },
      });
    } catch {
      // Toast error is handled globally
    } finally {
      clearTimeoutAndHideModal(loadingTimeout);
    }

    setRouteLocked(!routeLocked);
  };

  const handleMarkerClick = useCallback(
    (marker: MapMarker, isShiftPressed: boolean) => {
      const currentAddresses = addressesRef.current;
      const currentRoutes = routesRef.current;
      const currentSelectedAddresses = selectedAddressesRef.current;
      const address = currentAddresses.find((a) => a.id === marker.id);
      if (address) {
        // Check if user is clicking on a different type of address than current selection
        const clickedSelectionType = getSelectionType([address]);
        const currentSelectionType = getSelectionType(currentSelectedAddresses);

        // If switching between assigned and unassigned, clear previous selection
        if (
          !isShiftPressed &&
          currentSelectionType !== "empty" &&
          clickedSelectionType !== "empty" &&
          currentSelectionType !== clickedSelectionType
        ) {
          resetAllSelected();
        }

        // Check if the clicked address is unassigned and update filters accordingly
        let isUnassigned = !address.region;

        // Special handling for onDemand addresses
        // OnDemand addresses may have a region but not receive service for this shift
        if (!isUnassigned && address.isOnDemand) {
          // Check if the address exists in any of the current routes
          const existsInRoutes = currentRoutes.some((route) =>
            route.locations?.some(
              (location) => location.locationId === address.id,
            ),
          );

          // If it doesn't exist in any route, treat it as unassigned
          if (!existsInRoutes) {
            isUnassigned = true;
          }
        }

        if (isUnassigned && address.customer) {
          // Determine if the customer is active or inactive based on:
          // 1. Customer service access
          // 2. Address service access
          // 3. Customer email verified status
          // All three must be true for the location to be considered active
          const isActive =
            address.customer.serviceAccess &&
            address.serviceAccess !== false && // Consider undefined as true for backward compatibility
            address.customer.emailVerified;

          // Update the appropriate unassigned filter to make the address visible on the map
          setQueryParams((prevParams) => ({
            ...prevParams,
            unassignedActive: isActive ? true : prevParams.unassignedActive,
            unassignedInactive: !isActive
              ? true
              : prevParams.unassignedInactive,
          }));
        }

        setSelectedAddress([address], isShiftPressed);
      }
    },
    [setSelectedAddress, resetAllSelected, setQueryParams],
  );

  const handleSelectedMarkersChange = useCallback(
    (selectedMarkers: MapMarker[], event?: MouseEvent) => {
      // Implement selection change logic for RoutePlannerPage
      const selectedAddresses = selectedMarkers;

      // Check the selection type
      const selectionType = getSelectionType(selectedAddresses);

      if (selectionType === "mixed" && selectedAddresses.length > 1 && event) {
        // Show context menu for mixed selection using local state
        setContextMenuState({
          open: true,
          position: { top: event.clientY, left: event.clientX },
          mixedSelection: selectedAddresses,
        });
        return; // Don't proceed with normal selection until user makes choice
      }

      // For non-mixed selections, proceed with normal selection logic.
      setDirectSelection(selectedAddresses);
      setSidebar(selectedAddresses.length > 0);
      // Zoom/center to fit the lassoed selection. Safe now that
      // fitMapToMarkers no longer snaps to the world view first.
      if (selectedAddresses.length > 0) {
        setMapCentering(undefined, selectedAddresses);
      }
    },
    [setDirectSelection, setSidebar, setMapCentering],
  );

  const handleEndShiftConfirm = () => {
    const parsedDate = DateTime.fromFormat(date as string, "MM-dd-yyyy");
    const nextDay = parsedDate.plus({ days: 1 });
    setEndShiftStatus(undefined); // Reset status before navigating
    handleDateChange(nextDay);
    closeModal();
  };

  const memoizedSearchData = useMemo(() => {
    return searchData?.data?.map((location) => ({
      id: location.id,
      latitude: location.latitude,
      longitude: location.longitude,
      address: location.address,
      locationSequence: location.locationSequence,
      customer: location.customer,
      region: location.region,
      serviceAccess: location.serviceAccess,
      isFlatRate: location.type === "annual",
      isOnDemand: location.type === "onDemand",
      isSnowReporter: location.reportsEnabled,
      selected: false,
    }));
  }, [searchData?.data]);

  const handleAddCustomer = useCallback(
    (searchedAddress: string) => {
      setSearchedAddressForCustomer(searchedAddress);
      setAddCustomerModalOpen(true);
    },
    [setSearchedAddressForCustomer],
  );

  const handleSearchResultSelect = useCallback(
    (selectedMarker: MapMarker) => {
      // Apply the same logic as handleMarkerClick for filter updates
      let address =
        addresses.find((a) => a.id === selectedMarker.id) || selectedMarker;

      // Check if the selected address is unassigned and update filters accordingly
      let isUnassigned = !address.region;

      // Special handling for onDemand addresses
      // OnDemand addresses may have a region but not receive service for this shift
      if (!isUnassigned && (address.isOnDemand || selectedMarker.isOnDemand)) {
        // Check if the address exists in any of the current routes
        const existsInRoutes = routes.some((route) =>
          route.locations?.some(
            (location) => location.locationId === address.id,
          ),
        );

        // If it doesn't exist in any route, treat it as unassigned for filtering
        // but preserve the actual region information from the search result
        if (!existsInRoutes) {
          isUnassigned = true;
          // Use the selectedMarker (search result) which has the correct region info
          // instead of the address from the addresses array which might show "Unassigned"
          if (selectedMarker.region) {
            address = {
              ...address,
              region: selectedMarker.region,
            };
          }
        }
      }

      if (isUnassigned && address.customer) {
        // Determine if the customer is active or inactive based on:
        // 1. Customer service access
        // 2. Address service access
        // 3. Customer email verified status
        // All three must be true for the location to be considered active
        const isActive =
          address.customer.serviceAccess &&
          address.serviceAccess !== false && // Consider undefined as true for backward compatibility
          address.customer.emailVerified;

        // Update the appropriate unassigned filter to make the address visible on the map
        setQueryParams((prevParams) => ({
          ...prevParams,
          unassignedActive: isActive ? true : prevParams.unassignedActive,
          unassignedInactive: !isActive ? true : prevParams.unassignedInactive,
        }));
      }

      // Set the selected address and center the map
      setSelectedAddress([address], true);
      setMapCentering(undefined, [address]);
    },
    [addresses, routes, setQueryParams, setSelectedAddress, setMapCentering],
  );

  const handleCustomerCreated = (newLocationData?: {
    id: string;
    latitude: number;
    longitude: number;
    address: string;
  }) => {
    toast.success("Customer created successfully!");

    // Store the location data for later use
    if (newLocationData) {
      // Store in a ref or state to be used after unassigned data loads
      setSearchedAddressForCustomer(newLocationData.address);
    }

    // Enable the "Unassigned Inactive" filter
    setQueryParams((prevParams) => ({
      ...prevParams,
      unassignedInactive: true,
    }));

    // The focusing will happen automatically via useEffect when addresses update
  };

  const memoizedMapFilters = useMemo(
    () => (
      <RoutePlannerMapFilters
        onShowSkippedChange={(val: boolean) =>
          handleStatusChange(AvailableStatuses.Skipped, val)
        }
        onShowRegularChange={(val: boolean) =>
          handleStatusChange(AvailableStatuses.Regular, val)
        }
        onShowDelayedChange={(val: boolean) =>
          handleStatusChange(AvailableStatuses.Delayed, val)
        }
        onShowFlatRateChange={(val: boolean) =>
          handleLocationTypeChange("annual", val)
        }
        onShowOnDemandChange={(val: boolean) =>
          handleLocationTypeChange("onDemand", val)
        }
        onShowUnassignedActiveChange={(val: boolean) =>
          handleUnassignedFilterChange("active", val)
        }
        onShowUnassignedInactiveChange={(val: boolean) =>
          handleUnassignedFilterChange("inactive", val)
        }
        onBulkEditStartTime={() => openModal(Modals.BULK_EDIT_TIME)}
        showSkipped={
          queryParams.orderStatuses?.includes(AvailableStatuses.Skipped) ||
          false
        }
        showRegular={
          queryParams.orderStatuses?.includes(AvailableStatuses.Regular) ||
          false
        }
        showDelayed={
          queryParams.orderStatuses?.includes(AvailableStatuses.Delayed) ||
          false
        }
        showFlatRate={queryParams.locationTypes?.includes("annual") || false}
        showOnDemand={queryParams.locationTypes?.includes("onDemand") || false}
        showUnassignedActive={queryParams.unassignedActive || false}
        showUnassignedInactive={queryParams.unassignedInactive || false}
      />
    ),
    [
      queryParams.orderStatuses,
      queryParams.locationTypes,
      queryParams.unassignedActive,
      queryParams.unassignedInactive,
      handleStatusChange,
      handleLocationTypeChange,
      handleUnassignedFilterChange,
      openModal,
    ],
  );

  const mapProps: MapComponentProps = useMemo(
    () => ({
      mapWidth: "100%",
      mapHeight,
      markers: addresses,
      driverMarkers:
        currentRouteStatus === RouteStatus.Live ? driverMarkers : [],
      mapZoom,
      drawing,
      lassoMode,
      mapCentering,
      query,
      selectedAddresses,
      isLoading,
      isSearchLoading: isLoadingSearchData && isFetchingSearchData,
      searchedAddresses: memoizedSearchData as MapMarker[],
      setQuery,
      setMapCentering: setMapCentering,
      onMapZoomChange: setMapZoom,
      onDrawingToggle: toggleDrawing,
      onSelectedMarkersChange: handleSelectedMarkersChange,
      onMarkerClick: handleMarkerClick,
      onSearchResultSelect: handleSearchResultSelect,
      setSelectedAddress,
      onResetSelection: resetAllSelected,
      onAddCustomer: handleAddCustomer,
      mapFilters: memoizedMapFilters,
      hasLiveStateChanges:
        hasLiveStateChanges && currentRouteStatus === RouteStatus.Live,
      hasPlannedChanges:
        hasPlanningChanges && currentRouteStatus === RouteStatus.Planned,
    }),
    [
      mapHeight,
      addresses,
      currentRouteStatus,
      driverMarkers,
      mapZoom,
      drawing,
      lassoMode,
      mapCentering,
      query,
      selectedAddresses,
      isLoading,
      isLoadingSearchData,
      isFetchingSearchData,
      memoizedSearchData,
      setQuery,
      setMapCentering,
      setMapZoom,
      toggleDrawing,
      handleSelectedMarkersChange,
      handleMarkerClick,
      handleSearchResultSelect,
      setSelectedAddress,
      resetAllSelected,
      handleAddCustomer,
      memoizedMapFilters,
      hasLiveStateChanges,
      hasPlanningChanges,
    ],
  );

  const onActionType = (type: MapActionTypeEnum) => {
    if (
      type === MapActionTypeEnum.markAsCompleted ||
      type === MapActionTypeEnum.markAsPartiallyCompleted ||
      type === MapActionTypeEnum.markAsCantPlow
    ) {
      handleUpdateDriverStatuses(type);
    }

    if (type === MapActionTypeEnum.markAsToBePlowed) {
      const hasSkippedSelection = selectedAddresses.some(
        (address) => address.order?.status === OrderStatuses.Skipped,
      );

      // Skipped orders have no valid live sequence to fall back on - route
      // them through the Move Addresses modal so the admin picks a real
      // position instead of firing a no-op driver-status update.
      if (hasSkippedSelection) {
        return setMoveDialog({ open: true, step: 0 });
      }

      handleUpdateDriverStatuses(type);
    }

    if (type === MapActionTypeEnum.reverseSequence) {
      if (selectedAddresses.length < 1) return;
      return openModal(Modals.REVERSE);
    }

    if (type === MapActionTypeEnum.moveAddresses) {
      return setMoveDialog({ open: true, step: 0 });
    }
    if (
      type === MapActionTypeEnum.moveToRegular ||
      type === MapActionTypeEnum.moveToDelayed ||
      type === MapActionTypeEnum.moveToSkipped
    ) {
      handleUpdateRouteOrder(type);
    }

    if (type === MapActionTypeEnum.sendNotification) {
      handleSendNotification();
    }
  };

  const handleUpdateRouteOrder = async (
    type: AdminGetRoutesLocationCustomerOrderResDto["status"],
  ) => {
    if (!selectedAddresses.length) {
      toast.error("No selected addresses found!");
      return;
    } else {
      const loadingTimeout = showLoadingScreenAfterThreeSeconds();
      try {
        await updateRouteCustomerOrders({
          body: {
            locations: selectedAddresses.map((address) => address.id),
            status: type,
          },
          pathParams: {
            date: selectedDate.toFormat("yyyy-MM-dd"),
          },
        });
      } catch {
        // Toast error is handled globally
      } finally {
        clearTimeoutAndHideModal(loadingTimeout);
      }
    }
  };

  const handleSendNotification = () => {
    if (!selectedAddresses.length) {
      toast.error("No selected addresses found!");
      return;
    }

    // Get all customer-location pairs from selected addresses
    // Include ALL selected addresses, even if multiple addresses belong to the same customer
    const customerLocationPairs: Array<{
      customerId: string;
      locationId: string;
    }> = [];

    selectedAddresses.forEach((address) => {
      if (address.customer?.id && address.id) {
        customerLocationPairs.push({
          customerId: address.customer.id,
          locationId: address.id,
        });
      }
    });

    if (customerLocationPairs.length === 0) {
      toast.error("No valid customers found in selected addresses!");
      return;
    }

    // Navigate to Create Notification screen with pre-selected recipients
    // Format: customerId:locationId,customerId:locationId,...
    // Multiple addresses for the same customer will result in multiple recipient entries
    const recipientsParam = customerLocationPairs
      .map(({ customerId, locationId }) => `${customerId}:${locationId}`)
      .join(",");

    const params = new URLSearchParams();
    params.set("recipients", recipientsParam);
    navigate(`${AdRoutes.addNotification}?${params.toString()}`);
  };

  const handleUpdateDriverStatuses = (
    type: AdminGetRoutesLocationsResDto["driverStatus"],
  ) => {
    if (!selectedAddresses.length) {
      toast.error("No selected addresses found!");
      return;
    }

    if (!type) {
      toast.error("Please select a driver status!");
      return;
    }

    const shouldExcludeInProgress = hasInProgressLocations(selectedAddresses);

    const filteredAddresses = shouldExcludeInProgress
      ? selectedAddresses.filter(
          (address) => address.driverStatus !== "inProgress",
        )
      : selectedAddresses;

    updateDriverStatuses({
      body: {
        locations: filteredAddresses.map((address) => address.id),
        status: type,
        timezone: DateTime.local().zoneName,
      },
      pathParams: {
        date: selectedDate.toFormat("yyyy-MM-dd"),
      },
    });
  };

  const onRouteClick = (val: string) => {
    setMapCentering(val);
  };

  const handleAddVirtualRoute = (index: number) => {
    setNewVirtualRoutePriority(index);
    openModal(Modals.CREATE_VIRTUAL_ROUTE);
  };

  const handleDeleteVirtualRoute = (routeId: string, routeName: string) => {
    setPendingVirtualRouteDelete({
      routeId,
      routeName,
    });
    openModal(Modals.DELETE_VIRTUAL_ROUTE);
  };

  const onDispatchDisabledConfirmation = () => {
    closeModal();

    if (!liveRouteData?.data && !liveRouteData?.data?.date) {
      toast.error("No live route found!");
      return;
    }

    const date = DateTime.fromISO(
      liveRouteData?.data?.date as unknown as string,
    );
    const formattedDate = date.toFormat("yyyy-MM-dd");

    setSelectedDate(date);
    setQueryParams({
      ...queryParams,
      date: formattedDate,
    });

    navigate(`/route-planner/${date.toFormat("MM-dd-yyyy")}`, {
      state: { internalNavigation: true },
    });
  };

  const onCreateRoutesDisabledConfirmation = () => {
    closeModal();

    if (!plannedRouteData?.data && !plannedRouteData?.data?.date) {
      toast.error("No planned route found!");
      return;
    }

    const date = DateTime.fromISO(
      plannedRouteData?.data?.date as unknown as string,
    );
    const formattedDate = date.toFormat("yyyy-MM-dd");

    setSelectedDate(date);
    setQueryParams({
      ...queryParams,
      date: formattedDate,
    });

    navigate(`/route-planner/${date.toFormat("MM-dd-yyyy")}`, {
      state: { internalNavigation: true },
    });
  };

  const onPendingDriverUpdate = async () => {
    closeModal();
    if (!pendingDriverUpdate) return;
    try {
      await updateRoute({
        body: {
          driverId: ["Any", "No Driver"].includes(pendingDriverUpdate.driverId)
            ? null
            : pendingDriverUpdate.driverId,
          startTime: pendingDriverUpdate.startTime,
          availableForAnyDriver: pendingDriverUpdate.driverId === "Any",
        },
        pathParams: {
          routeId: pendingDriverUpdate.routeId,
        },
      });
    } catch (error) {
      if ((error as unknown as { httpStatus: string })?.httpStatus === "409") {
        setActiveModal(Modals.CANNOT_UPDATE_ROUTE);
      } else {
        console.error("Unexpected error:", error);
      }
    }
  };

  const onResetChangesConfirm = () => {
    if (currentRouteStatus === RouteStatus.Live) {
      rollback({
        pathParams: {
          date: selectedDate.toFormat("yyyy-MM-dd"),
        },
      });
    } else if (
      currentRouteStatus === RouteStatus.Planning ||
      currentRouteStatus === RouteStatus.Planned
    ) {
      reset({
        pathParams: {
          date: selectedDate.toFormat("yyyy-MM-dd"),
        },
      });
      setHasPlanningChanges(false); // Clear changes after reset
    } else return;
    closeModal();
  };

  const onPendingVirtualRouteDelete = async () => {
    closeModal();

    const loadingTimeout = showLoadingScreenAfterThreeSeconds();

    try {
      await deleteVirtualRoute({
        pathParams: {
          routeId: pendingVirtualRouteDelete?.routeId ?? "",
        },
      });
    } catch {
      // Toast error is handled globally
    } finally {
      clearTimeoutAndHideModal(loadingTimeout);
    }

    closeModal();
  };

  const onConfirmRevertToPlanning = async () => {
    closeModal();

    setTransitioningRoutesPhase({
      phase: "revert",
      status: "loading",
    });

    revertToPlanning({
      pathParams: {
        date: selectedDate.toFormat("yyyy-MM-dd"),
      },
      body: {
        state: "planning",
      },
    });
  };

  const handleRoutePhaseTransition = async (config: {
    modal: Modals;
    transitionPhase: "build" | "live";
    actionFunction: (params: {
      pathParams: { date: string };
      queryParams?: { timezone?: string };
    }) => void;
  }) => {
    const { modal, transitionPhase, actionFunction } = config;

    let result;
    if (transitionPhase === "build") {
      result = await refetchPlannedRouteData();
    } else if (transitionPhase === "live") {
      result = await refetchLiveRouteData();
    }

    if (result?.data?.data && result?.data?.data?.date) {
      openModal(modal);
      return;
    }

    if (hasNoDriverAssigned) {
      toast.error(
        "Please assign a Driver or Any to every Route before moving forward.",
      );
      return;
    }

    if (hasInvalidStartTime) {
      toast.error("Please Change Start Time of All Routes to a Future Time");
      return;
    }

    // Reset planning changes when transitioning to build phase (Create Routes)
    // All calculations have already happened, so no need to show warning
    if (transitionPhase === "build") {
      setHasPlanningChanges(false);
    }

    setTransitioningRoutesPhase({
      phase: transitionPhase,
      status: "loading",
    });

    actionFunction({
      pathParams: {
        date: selectedDate.toFormat("yyyy-MM-dd"),
      },
      queryParams: {
        timezone: DateTime.local().zoneName,
      },
    });
  };

  const onPendingMoveOrders = async () => {
    setMoveOrdersDialogOpen(false);

    const loadingTimeout = showLoadingScreenAfterThreeSeconds();
    try {
      if (currentRouteStatus === RouteStatus.Planning) {
        await updateRouteCustomerOrders({
          body: {
            locations: moveOrders.locationIds.map(
              (location) => location.locationId,
            ),
            status: moveOrders.targetRouteOrderStatus,
          },
          pathParams: {
            date: selectedDate.toFormat("yyyy-MM-dd"),
          },
        });
      } else {
        await transferOrders({
          body: {
            ...moveOrders,
            targetDate: selectedDate.toFormat("yyyy-MM-dd"),
          },
          pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
        });
      }
    } catch (error) {
      if ((error as unknown as { httpStatus: string })?.httpStatus === "409") {
        setActiveModal(Modals.CANNOT_UPDATE_ROUTE);
      } else {
        console.error("Unexpected error:", error);
      }
    } finally {
      clearTimeoutAndHideModal(loadingTimeout);
    }
    closeModal();
  };

  const onConfirmMoveUnassignedToRoute = async () => {
    if (!pendingUnassignedMove) return;

    const pendingMoveRef = pendingUnassignedMove; // Store reference before clearing
    setActiveModal(null);
    setPendingUnassignedMove(undefined);

    const loadingTimeout = showLoadingScreenAfterThreeSeconds();
    try {
      await pendingMoveRef.mutationFn({
        body: {
          ...pendingMoveRef.moveOrders,
          targetDate: pendingMoveRef.selectedDate,
        },
        pathParams: { date: pendingMoveRef.selectedDate },
      });

      // Execute the success callback (handles toast, reset, and refetch)
      if (pendingMoveRef.onSuccess) {
        pendingMoveRef.onSuccess();
      }
    } catch (error) {
      if ((error as unknown as { httpStatus: string })?.httpStatus === "409") {
        setActiveModal(Modals.CANNOT_UPDATE_ROUTE);
      } else {
        console.error("Unexpected error:", error);
      }
    } finally {
      clearTimeoutAndHideModal(loadingTimeout);
    }
  };

  const {
    values,
    handleBlur,
    submitForm,
    setFieldTouched,
    setFieldValue,
    isValid,
    dirty,
    validateForm,
    resetForm,
  } = useFormik<AdminPatchRouteStartTimeReqDto>({
    initialValues: {
      startTime: "",
    },
    validationSchema: toFormikValidationSchema(
      adminPatchRouteStartTimeReqDtoSchema,
    ),
    onSubmit: async () => {
      const loadingTimeout = showLoadingScreenAfterThreeSeconds();

      try {
        await bulkEditStartTime({
          pathParams: {
            date: selectedDate.toFormat("yyyy-MM-dd"),
          },
          body: {
            startTime: values.startTime,
            timezone: DateTime.local().zoneName,
          },
        });
      } catch {
        // Toast error is handled globally
      } finally {
        clearTimeoutAndHideModal(loadingTimeout);
      }
      closeModal();
      resetForm();
    },
  });

  const hasNoDriverAssigned = unfilteredRoutesData?.data?.some(
    (route) => route.driverId === null && !route.availableForAnyDriver,
  );

  // Handle disabled button clicks to show appropriate toast messages
  const handleDisabledCreateRoutesClick = () => {
    if (hasInvalidStartTime) {
      toast.error("Please Change Start Time of All Routes to a Future Time");
    } else if (routes?.[0]?.isLocked) {
      toast.error("Cannot create routes while route is locked.");
    }
  };

  const handleDisabledDispatchClick = () => {
    if (hasInvalidStartTime) {
      toast.error("Please Change Start Time of All Routes to a Future Time");
    }
  };

  const renderRoutePhaseButtons = () => {
    const isResetDisabled =
      currentRouteStatus === RouteStatus.Live
        ? !hasLiveStateChanges || isCalculatingEstTime || isCommittingLiveRoutes
        : !hasPlanningChanges || isCalculatingEstTime;

    const resetChangesButton = (
      <Button
        variant="outlined"
        onClick={() => {
          openModal(Modals.CONFIRM_RESET);
        }}
        startIcon={<RestoreOutlined />}
        disabled={isResetDisabled}
      >
        Reset
      </Button>
    );

    switch (currentRouteStatus) {
      case RouteStatus.Planning:
        return (
          <Box sx={styles.phaseActions}>
            {resetChangesButton}
            <ClickableDisabledButton
              variant="outlined"
              onClick={() =>
                handleRoutePhaseTransition({
                  modal: Modals.CREATE_ROUTES_DISABLED,
                  transitionPhase: "build",
                  actionFunction: createRoutes,
                })
              }
              onDisabledClick={handleDisabledCreateRoutesClick}
              disabled={routes?.[0]?.isLocked || hasInvalidStartTime || false}
              startIcon={<TurnSharpRightOutlined />}
            >
              Create Routes
            </ClickableDisabledButton>
          </Box>
        );

      case RouteStatus.Planned:
        return (
          <Box sx={styles.phaseActions}>
            {resetChangesButton}
            <Button
              variant="outlined"
              onClick={() => {
                triggerEstCalculation({
                  pathParams: {
                    date: selectedDate.toFormat("yyyy-MM-dd"),
                  },
                });
              }}
              disabled={!hasPlanningChanges || isCalculatingEstTime}
              startIcon={<RefreshOutlined />}
            >
              Update Values
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Check for invalid start times and show toast if needed
                if (hasInvalidStartTime) {
                  handleDisabledDispatchClick();
                  return; // Early return - prevent dispatch functionality
                }

                // Proceed with normal dispatch functionality
                handleRoutePhaseTransition({
                  modal: Modals.DISPATCH_DISABLED,
                  transitionPhase: "live",
                  actionFunction: dispatchRoutes,
                });
              }}
              startIcon={<SendOutlined />}
            >
              Dispatch
            </Button>
          </Box>
        );

      case RouteStatus.Live:
        return (
          <Box sx={styles.phaseActions}>
            {resetChangesButton}
            <Button
              variant="contained"
              onClick={() => {
                commit({
                  pathParams: {
                    date: selectedDate.toFormat("yyyy-MM-dd"),
                  },
                });
              }}
              startIcon={
                isCommittingLiveRoutes ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <ManageAccountsOutlined />
                )
              }
              disabled={!hasLiveStateChanges || isCommittingLiveRoutes}
            >
              Update Routes
            </Button>
          </Box>
        );

      default:
        return null; // Or a fallback component
    }
  };

  // Show loading state while determining initial redirect
  if (isRedirecting) {
    return (
      <DashboardContentLayout flexedContainer={false} hasBottomPadding>
        <Navbar showAccountMenu />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
          }}
        >
          <Typography>Loading...</Typography>
        </Box>
      </DashboardContentLayout>
    );
  }

  return (
    <SocketContextProvider
      routePlannerDate={selectedDate.toFormat("yyyy-MM-dd")}
      currentPage="route-planner"
    >
      <DashboardContentLayout flexedContainer={sidebar} hasBottomPadding>
        <Navbar showAccountMenu>
          <RoutePlannerToolbarActions
            routesData={unfilteredRoutesData}
            handleRefreshRoute={onRefreshRoute}
            isRefreshing={isRefreshing}
            openModal={openModal}
            currentRouteStatus={currentRouteStatus}
            routeLocked={routeLocked}
            renderRoutePhaseButtons={renderRoutePhaseButtons}
            selectedDate={selectedDate}
            handleDateChange={handleDateChange}
            lockRoute={lockRoute}
          />
        </Navbar>
        <Box
          sx={
            {
              ...styles.resizableContentWrapper,
              ...(sidebar ? styles.hasSidebar : styles.noSidebar),
            } as SxProps<Theme>
          }
        >
          <Box sx={{ position: "relative" }}>
            <MapProvider>
              <MapComponent {...mapProps} />
            </MapProvider>

            {/* Black overlay for mixed selection */}
            {contextMenuState.open && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1000,
                  cursor: "pointer",
                }}
                onClick={() =>
                  setContextMenuState({
                    open: false,
                    position: null,
                    mixedSelection: [],
                  })
                }
              />
            )}
          </Box>

          <Box sx={styles.dragHandleWrapper} onMouseDown={onMouseDown}>
            <Box
              className="drag-handle"
              sx={merge(styles.dragHandler, isDragging && styles.onDragStyle)}
            />
          </Box>

          {enhancedRoutesData && (
            <RoutePlannerTable
              data={enhancedRoutesData}
              currentRouteStatus={currentRouteStatus}
              sortOptions={sortOptions}
              sortOptionsChange={setSortOptions}
              onClickedAddress={setClickedAddress}
              onRouteClick={(val: string) => onRouteClick(val)}
              onSelectRouteDriver={(
                routeId,
                routeName,
                startTime,
                driverId,
                driverName,
                currentDriverRoutes,
              ) => {
                setPendingDriverUpdate({
                  driverId,
                  driverName,
                  routeId,
                  routeName: routeName,
                  assignedRoutes:
                    currentDriverRoutes?.map(
                      (route) => route.region?.name ?? route.routeName ?? "",
                    ) || [],
                  startTime,
                });

                openModal(Modals.EDIT_ROUTE_DRIVER);
              }}
              onSelectStartTime={(
                routeId,
                startTime,
                availableForAnyDriver,
                driverId,
              ) => {
                updateRouteTime({
                  body: {
                    driverId,
                    startTime,
                    availableForAnyDriver: availableForAnyDriver,
                    timezone: DateTime.local().zoneName,
                  },
                  pathParams: {
                    routeId,
                  },
                });
              }}
              handleAddVirtualRoute={handleAddVirtualRoute}
              handleDeleteVirtualRoute={handleDeleteVirtualRoute}
              refetchData={refetch}
              isLoading={isLoading}
              queryParamDate={date}
              openModal={openModal}
              globalNotificationSettings={globalNotificationSettings?.data}
              onRealtimeAdjustmentsChange={(
                realtimeAdjustedServiceTimeMap,
                realtimeAdjustedEstimatedTimeMinutesMap,
              ) => {
                setAdjustedServiceTimeMap(realtimeAdjustedServiceTimeMap);
                setAdjustedEstimatedTimeMinutesMap(
                  realtimeAdjustedEstimatedTimeMinutesMap,
                );
              }}
            />
          )}
        </Box>
        {sidebar && (
          <SelectedAddresesSidebar
            customHeader={
              <>
                <Box sx={styles.sideBarTitleInfo}>
                  <Typography fontStyle="700" fontSize="16px" lineHeight="24px">
                    {selectedAddresses && selectedAddresses.length} selected
                  </Typography>
                  <Button onClick={resetAllSelected}>Reset</Button>
                </Box>
                <Box sx={styles.sideBarActions}>
                  <ControlledSelect
                    placeholderSelectEnabled={false}
                    placeholdeShow={false}
                    placeholder="Actions"
                    options={getBulkActions(
                      currentRouteStatus,
                      selectedAddresses,
                    )}
                    sx={{ width: "100%" }}
                    onChange={(value) =>
                      onActionType(value as MapActionTypeEnum)
                    }
                  />
                </Box>
              </>
            }
            excludeAddressValidation={shouldExcludeLocation}
            selectedAddresses={selectedAddresses}
            removeSelected={removeSelected}
            view="route-planner"
            serviceTimeMap={serviceTimeMap}
            adjustedServiceTimeMap={adjustedServiceTimeMap}
            adjustedEstimatedTimeMinutesMap={adjustedEstimatedTimeMinutesMap}
            currentRouteStatus={currentRouteStatus}
          />
        )}

        <TransitionRoutesPhaseModal
          status={transitioningRoutesPhase?.status}
          transitionPhase={transitioningRoutesPhase?.phase}
          open={Boolean(transitioningRoutesPhase?.status)}
          sx={styles.creatingRoutesDialogWidth}
          onClose={() => setTransitioningRoutesPhase(undefined)}
        />

        <ReverseSequenceDialog
          open={activeModal === Modals.REVERSE}
          sx={styles.creatingRoutesDialogWidth}
          onConfirm={() => {
            const routeId = selectedAddresses[0]?.routeId;
            if (routeId) {
              const shouldExcludeInProgress =
                hasInProgressLocations(selectedAddresses);

              const filteredAddresses = shouldExcludeInProgress
                ? selectedAddresses.filter(
                    (address) => address.status !== "inProgress",
                  )
                : selectedAddresses;

              reverseSequence({
                body: {
                  selectedRouteLocationIds: filteredAddresses.map(
                    (address) => address.id,
                  ),
                },
                pathParams: {
                  routeId: routeId,
                },
              });
            }
            closeModal();
          }}
          onClose={closeModal}
        />

        <EndShiftDialog
          open={activeModal === Modals.END_SHIFT}
          sx={styles.creatingRoutesDialogWidth}
          onSecondModalConfirm={handleEndShiftConfirm}
          onConfirm={async () => {
            try {
              if (date) {
                setEndShiftStatus("loading");

                await endShift({
                  pathParams: {
                    date: selectedDate.toFormat("yyyy-MM-dd"),
                  },
                });

                await new Promise((resolve) => {
                  const endShiftTimeout = setTimeout(() => {
                    setEndShiftStatus("success");
                    clearTimeout(endShiftTimeout);
                    resolve(null);
                  }, 3000);
                });
              }
            } catch {
              setEndShiftStatus(undefined);
              closeModal();
            }
          }}
          status={endShiftStatus}
          onClose={closeModal}
          remainingOrdersNr={notCompletedOrders}
        />

        <BulkEditStartTimeDialog
          title="Change Start Time For All Routes"
          onConfirm={submitForm}
          open={activeModal === Modals.BULK_EDIT_TIME}
          cancelText="Cancel"
          confirmText="Change Start Time"
          confirmButtonDisabled={!isValid || !dirty}
          selectedTime={values.startTime}
          children={
            <BulkEditStartTimeForm
              values={values}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              validateForm={validateForm}
              currentRouteStatus={currentRouteStatus}
            />
          }
          onClose={() => {
            closeModal();
            resetForm();
          }}
        />
        <MoveLocationsDialog
          open={moveDialog.open}
          onClose={() => {
            setRoutes(unfilteredRoutesData?.data ?? []);
            setMoveDialog({ open: false, step: -1 });
          }}
          onRefresh={refetch}
          invalidSelection={
            currentRouteStatus !== RouteStatus.Planning &&
            !hasAnyUnassignedAddresses &&
            (!sameRegionId || !validSequence)
          }
          shouldSequence={!validSequence || !sameRegionId}
          defaultActiveStep={moveDialog.step}
          sx={{ maxWidth: "900px" }}
        />

        <EditRouteDriverDialog
          sx={styles.resetChangesDialogWidth}
          pendingDriverUpdate={pendingDriverUpdate}
          hasOtherRoute={!!pendingDriverUpdate?.assignedRoutes.length || false}
          onConfirm={onPendingDriverUpdate}
          open={activeModal === Modals.EDIT_ROUTE_DRIVER}
          onClose={closeModal}
        />

        {activeModal === Modals.CREATE_VIRTUAL_ROUTE && (
          <CreateVirtualRouteDialog
            open={activeModal === Modals.CREATE_VIRTUAL_ROUTE}
            onClose={closeModal}
            onRefresh={refetch}
            currentRouteStatus={currentRouteStatus}
          />
        )}

        <ConfirmDialog
          title="Delete Virtual Route"
          description={`Are you sure you want to delete ${pendingVirtualRouteDelete?.routeName}? This will unassign all linked drivers and addresses and cannot be undone.?`}
          cancelText="Cancel"
          confirmText="Delete"
          open={activeModal === Modals.DELETE_VIRTUAL_ROUTE}
          onConfirm={onPendingVirtualRouteDelete}
          onClose={closeModal}
        />

        <ConfirmDialog
          title="Revert to Planning Phase"
          description="Are you sure you want to revert to the Planning Phase? This will revert all changes to their default daily statuses."
          cancelText="Cancel"
          confirmText="Revert"
          open={activeModal === Modals.REVERT_TO_PLANNING}
          onConfirm={onConfirmRevertToPlanning}
          onClose={closeModal}
          sx={styles.resetChangesDialogWidth}
        />

        <ConfirmDialog
          title={`Reset Changes in ${capitalizeFirstLetter(currentRouteStatus || "")} Phase`}
          description="Are you sure you want to reset? This will restore this phase to its initial state."
          cancelText="Cancel"
          confirmText="Reset"
          open={activeModal === Modals.CONFIRM_RESET}
          onConfirm={onResetChangesConfirm}
          onClose={closeModal}
          sx={styles.resetChangesDialogWidth}
        />

        <ConfirmDialog
          title="Cannot Update Address"
          description="Please update the status of the in progress order in the route, before conducting an action."
          confirmText="Confirm"
          open={activeModal === Modals.CANNOT_UPDATE_ROUTE}
          onConfirm={closeModal}
          onClose={closeModal}
          withCancelAction={false}
        />

        <ConfirmDialog
          title="Can't Create Routes"
          description="You can't create routes until the planned shift is live or canceled."
          cancelText="Cancel"
          confirmText="View Planned Shift"
          open={activeModal === Modals.CREATE_ROUTES_DISABLED}
          onConfirm={onCreateRoutesDisabledConfirmation}
          onClose={closeModal}
          withCancelAction={true}
        />

        <ConfirmDialog
          title="Dispatch Disabled"
          description="Please end the current live shift before starting a new one."
          cancelText="Cancel"
          confirmText="View Live Shift"
          open={activeModal === Modals.DISPATCH_DISABLED}
          onConfirm={onDispatchDisabledConfirmation}
          onClose={closeModal}
          withCancelAction={true}
        />

        {/* Notification Modals */}
        <RoutePlannerNotificationModals
          activeModal={activeModal}
          routes={routes}
          pendingNotificationUpdate={pendingNotificationUpdate}
          globalNotificationSettings={globalNotificationSettings?.data}
          selectedDate={selectedDate}
          onClose={closeModal}
        />

        {moveOrdersDialogOpen && (
          <ConfirmDialog
            title="Move to Route"
            description={`You are about to move ${moveOrders.locationIds.length} to ${moveOrders.targetRouteName} in the ${capitalizeFirstLetter(moveOrders.targetRouteOrderStatus)} shift${currentRouteStatus === RouteStatus.Planning ? "." : ` ${moveOrders.targetSeqNumber === 1 ? "before" : "after"} sequence ${moveOrders.visibleTargetSequence}.`}`}
            cancelText="Cancel"
            confirmText="Move"
            open={moveOrdersDialogOpen}
            onConfirm={onPendingMoveOrders}
            onClose={async () => {
              setRoutes(unfilteredRoutesData?.data ?? []);
              setMoveOrdersDialogOpen(false);
            }}
          />
        )}

        <ConfirmDialog
          title="Are you sure you want to move Unassigned Addresses into Route?"
          description="This action will update address type to On Demand and the order will remain active until the order status is updated in the current or future dates."
          cancelText="Cancel"
          confirmText="Confirm"
          open={activeModal === Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE}
          onConfirm={onConfirmMoveUnassignedToRoute}
          onClose={() => {
            setActiveModal(null);
            setPendingUnassignedMove(undefined);
          }}
        />

        <LoadingModal isLoading={isLoadingModalShown} />
      </DashboardContentLayout>
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        initialSearchedAddress={searchedAddressForCustomer}
        onCustomerCreated={handleCustomerCreated}
      />

      {/* Selection Context Menu for mixed assigned/unassigned selections will be handled with local state */}
      {contextMenuState.open && contextMenuState.position && (
        <Menu
          open={contextMenuState.open}
          onClose={() =>
            setContextMenuState({
              open: false,
              position: null,
              mixedSelection: [],
            })
          }
          anchorReference="anchorPosition"
          anchorPosition={contextMenuState.position}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: "#2A2A2A",
              border: "1px solid #444",
              borderRadius: "8px",
              minWidth: "180px",
              zIndex: 1001,
            },
            "& .MuiMenuItem-root": {
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#3A3A3A",
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              const { assigned } = categorizeAddresses(
                contextMenuState.mixedSelection,
              );
              setDirectSelection(assigned);
              setSidebar(assigned.length > 0);
              if (assigned.length > 0) {
                setMapCentering(undefined, assigned);
              }
              setContextMenuState({
                open: false,
                position: null,
                mixedSelection: [],
              });
            }}
          >
            <Typography variant="body2">Select Assigned</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              const { unassigned } = categorizeAddresses(
                contextMenuState.mixedSelection,
              );
              setDirectSelection(unassigned);
              setSidebar(unassigned.length > 0);
              if (unassigned.length > 0) {
                setMapCentering(undefined, unassigned);
              }
              setContextMenuState({
                open: false,
                position: null,
                mixedSelection: [],
              });
            }}
          >
            <Typography variant="body2">Select Unassigned</Typography>
          </MenuItem>
        </Menu>
      )}
    </SocketContextProvider>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  resizableContentWrapper: {
    minHeight: "calc(100vh - 90px)",
    maxHeight: "calc(100vh - 90px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  hasSidebar: {
    width: "calc(100% - 317px)",
  },
  dragHandler: {
    height: "4px",
    width: "40px",
    borderRadius: "4px",
    backgroundColor: palette.grey[300],
    margin: "6px auto 0",
  },
  onDragStyle: {
    backgroundColor: "#fff",
  },
  creatingRoutesDialogWidth: {
    "& .MuiPaper-root": {
      maxWidth: "400px",
    },
  },
  sideBarTitleInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  driverUpdateDialogWidth: {
    "& .MuiPaper-root": {
      minWidth: "400px",
    },
  },
  resetChangesDialogWidth: {
    "& .MuiPaper-root": {
      maxWidth: "400px",
    },
  },
  phaseActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  dragHandleWrapper: {
    width: "100%",
    cursor: "grabbing",
  },
}));

export default RoutePlannerPage;
